import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import RichTextEditor from '../common/RichTextEditor';
// import { RichTextEditor } from '@/components/RichTextEditor';

/**
 * SectionInfoEditor - Component for editing section-level information
 * Used for LC/RC exercises and attendance tests
 * 
 * @param {string} sectionType - Type of section (e.g., 'lc-exercise', 'rc-exercise', 'attendance-test')
 * @param {string} dayId - ID of the day
 * @param {Object} initialData - Initial section data
 * @param {Function} onSave - Callback when section info is saved
 */
const SectionInfoEditor = ({ 
  sectionType, 
  dayId, 
  initialData = {}, 
  onSave 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    timeLimit: '',
    ...initialData,
  });

  useEffect(() => {
    setFormData({
      title: '',
      description: '',
      instructions: '',
      timeLimit: '',
      ...initialData,
    });
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      toast.error('Section title is required');
      return;
    }

    setIsSaving(true);
    try {
      // Call the onSave callback with the form data
      if (onSave) {
        await onSave(formData);
      }
      toast.success('Section information saved successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save section info:', error);
      toast.error(error?.message || 'Failed to save section information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      description: '',
      instructions: '',
      timeLimit: '',
      ...initialData,
    });
    setIsEditing(false);
  };

  const getSectionTypeLabel = () => {
    switch (sectionType) {
      case 'lc-exercise':
        return 'Listening Comprehension';
      case 'rc-exercise':
        return 'Reading Comprehension';
      case 'attendance-test':
        return 'Attendance Test';
      default:
        return 'Section';
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              {getSectionTypeLabel()} - Section Information
            </Typography>
          </Box>
          {!isEditing && (
            <IconButton 
              color="primary" 
              onClick={() => setIsEditing(true)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        {isEditing ? (
          <Box>
            <TextField
              fullWidth
              label="Section Title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              sx={{ mb: 2 }}
              placeholder="e.g., Listening Comprehension Exercise"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 2 }}
              placeholder="Brief description of this section"
            />

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Section Info'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            {formData.title ? (
              <>
                <Typography variant="body1" fontWeight="medium" gutterBottom>
                  {formData.title}
                </Typography>
                {formData.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {formData.description}
                  </Typography>
                )}
                {formData.instructions && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Instructions:
                    </Typography>
                    <Box 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        '& p': { margin: 0 }
                      }}
                      dangerouslySetInnerHTML={{ __html: formData.instructions }}
                    />
                  </Box>
                )}
                {formData.timeLimit && (
                  <Typography variant="body2" color="text.secondary">
                    Time Limit: {formData.timeLimit} minutes
                  </Typography>
                )}
              </>
            ) : (
              <Alert severity="info">
                No section information added yet. Click the edit icon to add section details.
              </Alert>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SectionInfoEditor;
